/**
 * Stocktake · open-container levels (add-pos-recipes-and-cups §5).
 *
 * At count time the operator counts WHOLE containers, but a volumetric article
 * (LITERS / GRAMS) usually has one OPEN container partially poured. This panel
 * captures that measured open level per volumetric line, in base units (mL for
 * LITERS, g for GRAMS). The value seeds the open-container overlay's inter-count
 * window on finalize (a hard cumulative reset — the overlay stops extrapolating
 * pours across the count).
 *
 * FUNCTIONAL scaffold — plain AntD wired to the same line PATCH as the count
 * (``measured_open_base_units``). The parent owns the serialised mutation queue
 * + If-Match concurrency; this component is purely presentational and delegates
 * each commit through ``onCommit``. The design pass folds it into the per-row
 * counting UI.
 */

import { useEffect, useMemo, useState } from "react";

import { Collapse, Empty, InputNumber, Space, Typography } from "antd";

import type { ArticleRow, StocktakeLineRow } from "../../api/client";

const { Text } = Typography;

/** Base-unit label + container size (in base units) for a volumetric article.
 * Returns null for non-volumetric (COUNT / unset) articles — they have no open
 * level to measure. Mirrors ``app/pos/portion_units.py``: LITERS → mL (×1000),
 * GRAMS → g (×1). */
const volumetricInfo = (
  a: ArticleRow | undefined,
): { unit: "mL" | "g"; container: number | null } | null => {
  const type = (a?.unit_type_override ?? a?.unit_type ?? "").toLowerCase();
  const sizeStr = a?.unit_size_override ?? a?.unit_size ?? null;
  const size = sizeStr == null ? null : Number(sizeStr);
  if (type === "liters")
    return {
      unit: "mL",
      container: size != null && size > 0 ? Math.round(size * 1000) : null,
    };
  if (type === "grams")
    return {
      unit: "g",
      container: size != null && size > 0 ? Math.round(size) : null,
    };
  return null;
};

interface Props {
  lines: StocktakeLineRow[];
  articleByRoot: Map<number, ArticleRow>;
  nameByRoot: (chainRootId: number) => string;
  /** Commit (or clear, with ``null``) the measured open level for a line. */
  onCommit: (chainRootId: number, baseUnits: number | null) => Promise<void>;
  /** Post-finalize the overlay window is sealed — read-only. */
  disabled?: boolean;
}

/** One volumetric line's open-level input — local draft, commits on blur so it
 * doesn't fire a PATCH per keystroke. */
const OpenLevelRow = ({
  line,
  unit,
  container,
  name,
  onCommit,
  disabled,
}: {
  line: StocktakeLineRow;
  unit: "mL" | "g";
  container: number | null;
  name: string;
  onCommit: (chainRootId: number, baseUnits: number | null) => Promise<void>;
  disabled?: boolean;
}) => {
  const [draft, setDraft] = useState<number | null>(
    line.measured_open_base_units ?? null,
  );
  // Keep the draft in sync when the server value changes (e.g. a refetch after
  // another operator's edit) — but only when the input isn't being edited.
  useEffect(() => {
    setDraft(line.measured_open_base_units ?? null);
  }, [line.measured_open_base_units]);

  const dirty = (draft ?? null) !== (line.measured_open_base_units ?? null);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <Space direction="vertical" size={0}>
        <Text>{name}</Text>
        {container != null && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            full container = {container} {unit}
          </Text>
        )}
      </Space>
      <InputNumber
        style={{ width: 160 }}
        min={0}
        max={container ?? undefined}
        step={10}
        addonAfter={unit}
        placeholder="open level"
        value={draft ?? undefined}
        disabled={disabled}
        status={dirty ? "warning" : undefined}
        onChange={(v) => setDraft(v == null ? null : Number(v))}
        onBlur={() => {
          if (dirty) void onCommit(line.article_chain_root_id, draft);
        }}
        onPressEnter={() => {
          if (dirty) void onCommit(line.article_chain_root_id, draft);
        }}
      />
    </div>
  );
};

export const StocktakeOpenLevels = ({
  lines,
  articleByRoot,
  nameByRoot,
  onCommit,
  disabled,
}: Props) => {
  // Volumetric lines only — a COUNT article has no open level to measure.
  const volumetric = useMemo(
    () =>
      lines
        .map((line) => {
          const info = volumetricInfo(articleByRoot.get(line.article_chain_root_id));
          return info ? { line, ...info } : null;
        })
        .filter((x): x is NonNullable<typeof x> => x !== null),
    [lines, articleByRoot],
  );

  const measuredCount = volumetric.filter(
    (v) => v.line.measured_open_base_units != null,
  ).length;

  return (
    <Collapse
      items={[
        {
          key: "open-levels",
          label: (
            <Space>
              <Text strong>Open container levels</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {volumetric.length === 0
                  ? "no volumetric articles"
                  : `${measuredCount}/${volumetric.length} measured`}
              </Text>
            </Space>
          ),
          children:
            volumetric.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No LITERS/GRAMS articles on this stocktake — nothing to measure."
              />
            ) : (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Measure how much is left in the ONE open container per article
                  (the whole sealed containers are the count above). Seeds the
                  pour overlay so portion sales stop extrapolating across this
                  count.
                </Text>
                {volumetric.map((v) => (
                  <OpenLevelRow
                    key={v.line.article_chain_root_id}
                    line={v.line}
                    unit={v.unit}
                    container={v.container}
                    name={nameByRoot(v.line.article_chain_root_id)}
                    onCommit={onCommit}
                    disabled={disabled}
                  />
                ))}
              </Space>
            ),
        },
      ]}
    />
  );
};

export default StocktakeOpenLevels;
